import { css } from 'aphrodite';
import { addWeeks, subWeeks } from 'date-fns';
import { parseAsBoolean, parseAsIsoDate, parseAsString, parseAsStringEnum, useQueryState, useQueryStates } from 'nuqs';
import * as React from 'react';
import { DemoStatusFilter, IAccountTag, IUser } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { addUserToUserQueryCache } from '../../../queries';
import { useLeadPhoneCallOutcomesQuery } from '../../../queries/Lead/useLeadPhoneCallOutcomesQuery';
import { Button } from '../../../web/components-v2/Button';
import { ButtonGroup } from '../../../web/components-v2/ButtonGroup';
import { Calendar } from '../../../web/components-v2/Calendar';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemRadioIndicator,
	ComboboxList,
} from '../../../web/components-v2/Combobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { Popover, PopoverContent, PopoverTrigger } from '../../../web/components/Popover';
import { ChevronDown } from '../../../web/components/svgs/icons/ChevronDown';
import { XIconNew } from '../../../web/components/svgs/icons/XIcon';
import { TagCombobox, TagComboboxItem, TagComboboxStatus } from '../../../web/components/TagCombobox';
import {
	ManagerCombobox,
	ManagerComboboxStatus,
	UserCombobox,
	UserComboboxAvatarItem,
	UserComboboxStatus,
} from '../../../web/components/UserCombobox';
import { bs } from '../../../web/styles/styles';
import { FieldKey } from '../../viewModels/form';
import {
	ADD_ON_OPTIONS,
	DEAL_STAGE_OPTIONS,
	DEMO_STATUS_OPTIONS,
	FilterOption,
	INBOUND_CHANNEL_OPTIONS,
	INDUSTRY_OPTIONS,
	IS_COLD_DEMO_OPTIONS,
	LEAD_SOURCE_OPTIONS,
	LOST_REASON_OPTIONS,
	OVERBOOK_OPTIONS,
} from './constants';
import { styleSheet } from './styles';

export const Filters = () => {
	const id = React.useId();
	const userSession = useUserSession();
	const leadPhoneCallOutcomesQuery = useLeadPhoneCallOutcomesQuery();
	const [activeUserId, setActiveUserId] = useQueryState('userId', parseAsString);
	const [dateRange, setDateRange] = useQueryStates({
		from: parseAsIsoDate,
		to: parseAsIsoDate,
	});
	const [demoStatus, setDemoStatus] = useQueryState(
		'demoStatus',
		parseAsStringEnum<DemoStatusFilter>(Object.values(DemoStatusFilter)).withDefault(DemoStatusFilter.DemosToConfirm)
	);
	const [outcomeName, setOutcomeName] = useQueryState(
		'outcomeName',
		parseAsStringEnum<FieldKey>(Object.values(FieldKey)).withDefault(FieldKey.Unknown)
	);
	const [managerUserId, setManagerUserId] = useQueryState('managerUserId', parseAsString);
	const [demoPerformerUserId, setDemoPerformerUserId] = useQueryState('demoPerformerUserId', parseAsString);
	const [companyId, setCompanyId] = useQueryState('companyId', parseAsString);

	// Past Demos tab filter state
	const [dealStatus, setDealStatus] = useQueryState('dealStatus', parseAsString);
	const [leadSource, setLeadSource] = useQueryState('leadSource', parseAsString);
	const [addOn, setAddOn] = useQueryState('addOn', parseAsString);
	const [inboundChannel, setInboundChannel] = useQueryState('inboundChannel', parseAsString);
	const [lostReasonCategories, setLostReasonCategories] = useQueryState('lostReasonCategories', parseAsString);
	const [industry, setIndustry] = useQueryState('industry', parseAsString);
	const [tag, setTag] = useQueryState('tag', parseAsString);
	const [isColdDemo, setIsColdDemo] = useQueryState('isColdDemo', parseAsBoolean);
	const [overbook, setOverbook] = useQueryState('overbook', parseAsBoolean);
	const [followUpDateRange, setFollowUpDateRange] = useQueryStates({
		followUpDateStart: parseAsIsoDate,
		followUpDateEnd: parseAsIsoDate,
	});
	const [targetCloseDateRange, setTargetCloseDateRange] = useQueryStates({
		targetCloseDateStart: parseAsIsoDate,
		targetCloseDateEnd: parseAsIsoDate,
	});

	// Derive active tab from demoStatus
	const isSalesCoffeeAccount = userSession?.account?.isLevitateSalesCoffeeAccount;
	const isPastDemosTab = demoStatus === DemoStatusFilter.PastDemos;

	// Track if we've done the initial SDR prepopulation (to avoid re-populating after user clears)
	const hasInitializedSdrFilter = React.useRef(false);

	// Redirect non-sales-coffee accounts away from Past Demos tab
	React.useEffect(() => {
		if (isPastDemosTab && !isSalesCoffeeAccount) {
			setDemoStatus(DemoStatusFilter.DemosToConfirm);
		}
	}, [isPastDemosTab, isSalesCoffeeAccount, setDemoStatus]);

	// Prepopulate SDR filter with current user on upcoming demos tab (only on initial load)
	// Skip prepopulation when companyId is present (e.g. navigating from "view demo recordings" on a lead record)
	React.useEffect(() => {
		const currentUserId = userSession?.user?.id;
		if (!isPastDemosTab && !hasInitializedSdrFilter.current && !activeUserId && !companyId && currentUserId) {
			setActiveUserId(currentUserId);
			hasInitializedSdrFilter.current = true;
		}
	}, [isPastDemosTab, activeUserId, companyId, userSession?.user?.id, setActiveUserId]);

	// Shared function to clear all filter state
	const clearAllFilters = React.useCallback(() => {
		setActiveUserId(null);
		setManagerUserId(null);
		setDemoPerformerUserId(null);
		setDateRange({ from: null, to: null });
		setOutcomeName(null);
		setCompanyId(null);
		// Clear Past Demos tab filters
		setDealStatus(null);
		setLeadSource(null);
		setAddOn(null);
		setInboundChannel(null);
		setLostReasonCategories(null);
		setIndustry(null);
		setTag(null);
		setIsColdDemo(null);
		setOverbook(null);
		setFollowUpDateRange({ followUpDateStart: null, followUpDateEnd: null });
		setTargetCloseDateRange({ targetCloseDateStart: null, targetCloseDateEnd: null });
	}, [
		setActiveUserId,
		setManagerUserId,
		setDemoPerformerUserId,
		setDateRange,
		setOutcomeName,
		setCompanyId,
		setDealStatus,
		setLeadSource,
		setAddOn,
		setInboundChannel,
		setLostReasonCategories,
		setIndustry,
		setTag,
		setIsColdDemo,
		setOverbook,
		setFollowUpDateRange,
		setTargetCloseDateRange,
	]);

	// Tab switch handler - clears all filters when switching tabs
	const handleTabChange = React.useCallback(
		(tab: 'upcoming' | 'past') => {
			clearAllFilters();
			setDemoStatus(tab === 'past' ? DemoStatusFilter.PastDemos : DemoStatusFilter.DemosToConfirm);
		},
		[clearAllFilters, setDemoStatus]
	);

	// Keyboard handler for tab navigation
	const handleTabKeyDown = React.useCallback(
		(e: React.KeyboardEvent, tab: 'upcoming' | 'past') => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				handleTabChange(tab);
			} else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
				e.preventDefault();
				handleTabChange(tab === 'upcoming' ? 'past' : 'upcoming');
			}
		},
		[handleTabChange]
	);

	const handleUserChange = React.useCallback(
		(user: IUser) => {
			setActiveUserId(user?.id ?? null);
			// Clear manager selection since they're mutually exclusive
			if (user?.id) {
				addUserToUserQueryCache({ id: user.id, user });
				setManagerUserId(null);
			}
		},
		[setActiveUserId, setManagerUserId]
	);

	const handleCloserOwnerChange = React.useCallback(
		(user: IUser) => {
			setDemoPerformerUserId(user?.id || null);
		},
		[setDemoPerformerUserId]
	);

	const handleManagerChange = React.useCallback(
		(user: IUser) => {
			setManagerUserId(user?.id ?? null);
			// Clear user selection since they're mutually exclusive
			if (user?.id) {
				setActiveUserId(null);
			}
		},
		[setManagerUserId, setActiveUserId]
	);

	const handleOutcomeChange = React.useCallback(
		(newOutcomeName: FieldKey) => {
			setOutcomeName(newOutcomeName);
		},
		[setOutcomeName]
	);

	const handleClearAllFilters = React.useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			clearAllFilters();
			// Stay on current tab but reset demo status dropdown on Upcoming tab
			if (isPastDemosTab) {
				// Stay on Past Demos tab
				setDemoStatus(DemoStatusFilter.PastDemos);
			} else {
				// Reset to default on Upcoming tab
				setDemoStatus(DemoStatusFilter.DemosToConfirm);
			}
		},
		[clearAllFilters, isPastDemosTab, setDemoStatus]
	);

	const handleDemoStatusChange = (status: DemoStatusFilter) => {
		if (status === DemoStatusFilter.DemosToConfirm) {
			setOutcomeName(null);
		}
		if (status === DemoStatusFilter.DemosToReschedule) {
			setDateRange({ from: subWeeks(new Date(), 1), to: new Date() });
		}
		if (status === DemoStatusFilter.UpcomingDemos) {
			const currentDate = new Date();
			setDateRange({ from: currentDate, to: addWeeks(currentDate, 1) });
		}
		setDemoStatus(status);
	};

	// Check if any filters are active (for Upcoming tab, check if demo status changed from default)
	const hasActiveFilters =
		companyId ||
		activeUserId ||
		managerUserId ||
		demoPerformerUserId ||
		dateRange.from ||
		dateRange.to ||
		(!isPastDemosTab && demoStatus !== DemoStatusFilter.DemosToConfirm) ||
		outcomeName !== FieldKey.Unknown ||
		// Past Demos tab filters
		dealStatus ||
		leadSource ||
		addOn ||
		inboundChannel ||
		lostReasonCategories ||
		industry ||
		tag ||
		isColdDemo !== null ||
		overbook !== null ||
		followUpDateRange.followUpDateStart ||
		followUpDateRange.followUpDateEnd ||
		targetCloseDateRange.targetCloseDateStart ||
		targetCloseDateRange.targetCloseDateEnd;

	const outcomeItems: { label: string; value: FieldKey }[] = [{ label: 'All outcome types', value: FieldKey.Unknown }];
	leadPhoneCallOutcomesQuery.data?.forEach(outcome => {
		outcomeItems.push({
			label: outcome.label,
			value: outcome.name,
		});
	});

	return (
		<>
			{/* Tab header */}
			<div className={css(styleSheet.tabsContainer)} role='tablist' aria-label='Demo filters'>
				<div
					className={css(styleSheet.tab, !isPastDemosTab && styleSheet.tabActive)}
					onClick={() => handleTabChange('upcoming')}
					onKeyDown={e => handleTabKeyDown(e, 'upcoming')}
					role='tab'
					aria-selected={!isPastDemosTab}
					tabIndex={!isPastDemosTab ? 0 : -1}
				>
					Upcoming Demos / Demos to Reschedule
				</div>
				{isSalesCoffeeAccount && (
					<div
						className={css(styleSheet.tab, isPastDemosTab && styleSheet.tabActive)}
						onClick={() => handleTabChange('past')}
						onKeyDown={e => handleTabKeyDown(e, 'past')}
						role='tab'
						aria-selected={isPastDemosTab}
						tabIndex={isPastDemosTab ? 0 : -1}
					>
						Past Demos
					</div>
				)}
			</div>

			{/* Note about default date range */}
			<p className={css(bs.textSm, bs.textGray600)} style={{ marginBottom: 15 }}>
				Note: When Demo Date Range is not selected, we&apos;ll show demos from the past 14 days by default.
			</p>

			{/* Filter grid */}
			<div className={css(styleSheet.filter)}>
				<UserCombobox id={activeUserId} key={`sdr-${activeUserId}`} onValueChange={handleUserChange}>
					<div className={css(styleSheet.filterGroup)}>
						<label htmlFor={`${id}-sdr-combobox`} className={css(styleSheet.label)}>
							SDR
						</label>
						<ComboboxInput placeholder='Filter by SDR' id={`${id}-sdr-combobox`} showClear />
					</div>
					<ComboboxContent>
						<UserComboboxStatus />
						<ComboboxList>
							{(user: IUser) => <UserComboboxAvatarItem key={`sdr-item-${user.id}`} user={user} />}
						</ComboboxList>
					</ComboboxContent>
				</UserCombobox>
				{!isPastDemosTab && (
					<ManagerCombobox id={managerUserId} key={`managers-${managerUserId}`} onValueChange={handleManagerChange}>
						<div className={css(bs.flex, bs.flexCol, bs.gap1)}>
							<label htmlFor={`${id}-manager-combobox`} className={css(bs.textXs, bs.fontBold)}>
								Manager
							</label>
							<ComboboxInput placeholder='Filter by manager' id={`${id}-manager-combobox`} showClear />
						</div>
						<ComboboxContent>
							<ManagerComboboxStatus />
							<ComboboxList>
								{(user: IUser) => <UserComboboxAvatarItem key={`manager-item-${user.id}`} user={user} />}
							</ComboboxList>
						</ComboboxContent>
					</ManagerCombobox>
				)}
				<UserCombobox
					id={demoPerformerUserId}
					key={`closer-owner-${demoPerformerUserId}`}
					onValueChange={handleCloserOwnerChange}
					domain={isSalesCoffeeAccount ? 'levitateapp.com' : undefined}
				>
					<div className={css(styleSheet.filterGroup)}>
						<label htmlFor={`${id}-closer-combobox`} className={css(styleSheet.label)}>
							Closer owner
						</label>
						<ComboboxInput placeholder='Filter by closer owner' id={`${id}-closer-combobox`} showClear />
					</div>
					<ComboboxContent>
						<UserComboboxStatus domain={isSalesCoffeeAccount ? 'levitateapp.com' : undefined} />
						<ComboboxList>
							{(user: IUser) => <UserComboboxAvatarItem key={`closer-owner-item-${user.id}`} user={user} />}
						</ComboboxList>
					</ComboboxContent>
				</UserCombobox>
				<Popover>
					<div className={css(styleSheet.filterGroup)}>
						<label htmlFor='dates' className={css(styleSheet.label)}>
							Demo Date Range
						</label>
						<ButtonGroup styles={[styleSheet.dateButtonGroup]}>
							<PopoverTrigger asChild>
								<Button
									variant='outline'
									id='dates'
									styles={[styleSheet.dateButton, dateRange.from || dateRange.to ? styleSheet.dateButtonActive : null]}
								>
									{dateRange?.from && dateRange?.to
										? `${dateRange.from.toLocaleDateString()} - ${dateRange.to.toLocaleDateString()}`
										: 'Filter by Demo Date'}
									{!dateRange?.from && !dateRange?.to ? <ChevronDown /> : null}
								</Button>
							</PopoverTrigger>
							{dateRange.from || dateRange.to ? (
								<Button variant='outline' onClick={() => setDateRange({ from: null, to: null })}>
									<XIconNew />
								</Button>
							) : null}
						</ButtonGroup>
					</div>
					<PopoverContent styles={[bs.wAuto, bs.overflowHidden, bs.p0]} align='start'>
						<Calendar
							mode='range'
							selected={dateRange}
							numberOfMonths={2}
							timeZone='UTC'
							onSelect={r => setDateRange(r)}
						/>
					</PopoverContent>
				</Popover>
				{!isPastDemosTab && (
					<div className={css(styleSheet.filterGroup)}>
						<label htmlFor='demo-status' className={css(styleSheet.label)}>
							Demo Status
						</label>
						<Select value={demoStatus} onValueChange={handleDemoStatusChange} items={DEMO_STATUS_OPTIONS}>
							<SelectTrigger id='demo-status' styles={[bs.wFull]}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{DEMO_STATUS_OPTIONS.map(option => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				)}
				{/* Past Demos tab filters */}
				{isPastDemosTab && (
					<Combobox
						items={DEAL_STAGE_OPTIONS}
						value={DEAL_STAGE_OPTIONS.find(opt => opt.value === dealStatus) ?? null}
						onValueChange={item => setDealStatus((item as FilterOption)?.value ?? null)}
						itemToStringLabel={item => (item as FilterOption).label}
					>
						<div className={css(styleSheet.filterGroup)}>
							<label htmlFor={`${id}-deal-stage`} className={css(styleSheet.label)}>
								Deal Stage
							</label>
							<ComboboxInput placeholder='Filter by deal stage' id={`${id}-deal-stage`} showClear />
						</div>
						<ComboboxContent>
							<ComboboxEmpty>No results found.</ComboboxEmpty>
							<ComboboxList>
								{(item: { label: string; value: string }) => (
									<ComboboxItem key={item.value} value={item}>
										<ComboboxItemRadioIndicator />
										{item.label}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				)}
				{isPastDemosTab && (
					<Combobox
						items={LEAD_SOURCE_OPTIONS}
						value={LEAD_SOURCE_OPTIONS.find(opt => opt.value === leadSource) ?? null}
						onValueChange={item => setLeadSource((item as FilterOption)?.value ?? null)}
						itemToStringLabel={item => (item as FilterOption).label}
					>
						<div className={css(styleSheet.filterGroup)}>
							<label htmlFor={`${id}-lead-source`} className={css(styleSheet.label)}>
								Lead Source
							</label>
							<ComboboxInput placeholder='Filter by lead source' id={`${id}-lead-source`} showClear />
						</div>
						<ComboboxContent>
							<ComboboxEmpty>No results found.</ComboboxEmpty>
							<ComboboxList>
								{(item: { label: string; value: string }) => (
									<ComboboxItem key={item.label} value={item}>
										<ComboboxItemRadioIndicator />
										{item.label}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				)}
				{isPastDemosTab && (
					<Combobox
						items={INDUSTRY_OPTIONS}
						value={INDUSTRY_OPTIONS.find(opt => opt.value === industry) ?? null}
						onValueChange={item => setIndustry((item as FilterOption)?.value ?? null)}
						itemToStringLabel={item => (item as FilterOption).label}
					>
						<div className={css(styleSheet.filterGroup)}>
							<label htmlFor={`${id}-industry`} className={css(styleSheet.label)}>
								Industry
							</label>
							<ComboboxInput placeholder='Filter by industry' id={`${id}-industry`} showClear />
						</div>
						<ComboboxContent>
							<ComboboxEmpty>No results found.</ComboboxEmpty>
							<ComboboxList>
								{(item: { label: string; value: string }) => (
									<ComboboxItem key={item.value} value={item}>
										<ComboboxItemRadioIndicator />
										{item.label}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				)}
				{isPastDemosTab && (
					<Combobox
						items={ADD_ON_OPTIONS}
						value={ADD_ON_OPTIONS.find(opt => opt.value === addOn) ?? null}
						onValueChange={item => setAddOn((item as FilterOption)?.value ?? null)}
						itemToStringLabel={item => (item as FilterOption).label}
					>
						<div className={css(styleSheet.filterGroup)}>
							<label htmlFor={`${id}-add-on`} className={css(styleSheet.label)}>
								Add-On
							</label>
							<ComboboxInput placeholder='Filter by add-on' id={`${id}-add-on`} showClear />
						</div>
						<ComboboxContent>
							<ComboboxEmpty>No results found.</ComboboxEmpty>
							<ComboboxList>
								{(item: { label: string; value: string }) => (
									<ComboboxItem key={item.value} value={item}>
										<ComboboxItemRadioIndicator />
										{item.label}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				)}
				{isPastDemosTab && (
					<Combobox
						items={INBOUND_CHANNEL_OPTIONS}
						value={INBOUND_CHANNEL_OPTIONS.find(opt => opt.value === inboundChannel) ?? null}
						onValueChange={item => setInboundChannel((item as FilterOption)?.value ?? null)}
						itemToStringLabel={item => (item as FilterOption).label}
					>
						<div className={css(styleSheet.filterGroup)}>
							<label htmlFor={`${id}-inbound-channel`} className={css(styleSheet.label)}>
								Inbound Channel
							</label>
							<ComboboxInput placeholder='Filter by inbound channel' id={`${id}-inbound-channel`} showClear />
						</div>
						<ComboboxContent>
							<ComboboxEmpty>No results found.</ComboboxEmpty>
							<ComboboxList>
								{(item: { label: string; value: string }) => (
									<ComboboxItem key={item.value} value={item}>
										<ComboboxItemRadioIndicator />
										{item.label}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				)}
				{isPastDemosTab && (
					<Combobox
						items={LOST_REASON_OPTIONS}
						value={LOST_REASON_OPTIONS.find(opt => opt.value === lostReasonCategories) ?? null}
						onValueChange={item => setLostReasonCategories((item as FilterOption)?.value ?? null)}
						itemToStringLabel={item => (item as FilterOption).label}
					>
						<div className={css(styleSheet.filterGroup)}>
							<label htmlFor={`${id}-lost-reason`} className={css(styleSheet.label)}>
								Lost Reason
							</label>
							<ComboboxInput placeholder='Filter by lost reason' id={`${id}-lost-reason`} showClear />
						</div>
						<ComboboxContent>
							<ComboboxEmpty>No results found.</ComboboxEmpty>
							<ComboboxList>
								{(item: { label: string; value: string }) => (
									<ComboboxItem key={item.value} value={item}>
										<ComboboxItemRadioIndicator />
										{item.label}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				)}
				{isPastDemosTab && (
					<Combobox
						items={IS_COLD_DEMO_OPTIONS}
						value={IS_COLD_DEMO_OPTIONS.find(opt => opt.value === isColdDemo) ?? null}
						onValueChange={item => setIsColdDemo((item as FilterOption<boolean>)?.value ?? null)}
						itemToStringLabel={item => (item as FilterOption<boolean>).label}
					>
						<div className={css(styleSheet.filterGroup)}>
							<label htmlFor={`${id}-is-cold-demo`} className={css(styleSheet.label)}>
								Is Cold Demo
							</label>
							<ComboboxInput placeholder='Filter by cold demo' id={`${id}-is-cold-demo`} showClear />
						</div>
						<ComboboxContent>
							<ComboboxEmpty>No results found.</ComboboxEmpty>
							<ComboboxList>
								{(item: { label: string; value: boolean }) => (
									<ComboboxItem key={item.label} value={item}>
										<ComboboxItemRadioIndicator />
										{item.label}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				)}
				{isPastDemosTab && (
					<Combobox
						items={OVERBOOK_OPTIONS}
						value={OVERBOOK_OPTIONS.find(opt => opt.value === overbook) ?? null}
						onValueChange={item => setOverbook((item as FilterOption<boolean>)?.value ?? null)}
						itemToStringLabel={item => (item as FilterOption<boolean>).label}
					>
						<div className={css(styleSheet.filterGroup)}>
							<label htmlFor={`${id}-overbook`} className={css(styleSheet.label)}>
								Overbook
							</label>
							<ComboboxInput placeholder='Filter by overbook' id={`${id}-overbook`} showClear />
						</div>
						<ComboboxContent>
							<ComboboxEmpty>No results found.</ComboboxEmpty>
							<ComboboxList>
								{(item: { label: string; value: boolean }) => (
									<ComboboxItem key={item.label} value={item}>
										<ComboboxItemRadioIndicator />
										{item.label}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				)}
				{isPastDemosTab && (
					<Popover>
						<div className={css(styleSheet.filterGroup)}>
							<label htmlFor='follow-up-dates' className={css(styleSheet.label)}>
								Follow-Up Date Range
							</label>
							<ButtonGroup styles={[styleSheet.dateButtonGroup]}>
								<PopoverTrigger asChild>
									<Button
										variant='outline'
										id='follow-up-dates'
										styles={[
											styleSheet.dateButton,
											followUpDateRange.followUpDateStart || followUpDateRange.followUpDateEnd
												? styleSheet.dateButtonActive
												: null,
										]}
									>
										{followUpDateRange?.followUpDateStart && followUpDateRange?.followUpDateEnd
											? `${followUpDateRange.followUpDateStart.toLocaleDateString()} - ${followUpDateRange.followUpDateEnd.toLocaleDateString()}`
											: 'Filter by Follow-Up Date'}
										{!followUpDateRange.followUpDateStart && !followUpDateRange.followUpDateEnd ? (
											<ChevronDown />
										) : null}
									</Button>
								</PopoverTrigger>
								{followUpDateRange.followUpDateStart || followUpDateRange.followUpDateEnd ? (
									<Button
										variant='outline'
										onClick={() => setFollowUpDateRange({ followUpDateStart: null, followUpDateEnd: null })}
									>
										<XIconNew />
									</Button>
								) : null}
							</ButtonGroup>
						</div>
						<PopoverContent styles={[bs.wAuto, bs.overflowHidden, bs.p0]} align='start'>
							<Calendar
								mode='range'
								selected={{
									from: followUpDateRange.followUpDateStart,
									to: followUpDateRange.followUpDateEnd,
								}}
								numberOfMonths={2}
								timeZone='UTC'
								onSelect={r =>
									setFollowUpDateRange({ followUpDateStart: r?.from ?? null, followUpDateEnd: r?.to ?? null })
								}
							/>
						</PopoverContent>
					</Popover>
				)}
				{isPastDemosTab && (
					<Popover>
						<div className={css(styleSheet.filterGroup)}>
							<label htmlFor='target-close-dates' className={css(styleSheet.label)}>
								Target Close Date
							</label>
							<ButtonGroup styles={[styleSheet.dateButtonGroup]}>
								<PopoverTrigger asChild>
									<Button
										variant='outline'
										id='target-close-dates'
										styles={[
											styleSheet.dateButton,
											targetCloseDateRange.targetCloseDateStart || targetCloseDateRange.targetCloseDateEnd
												? styleSheet.dateButtonActive
												: null,
										]}
									>
										{targetCloseDateRange?.targetCloseDateStart && targetCloseDateRange?.targetCloseDateEnd
											? `${targetCloseDateRange.targetCloseDateStart.toLocaleDateString()} - ${targetCloseDateRange.targetCloseDateEnd.toLocaleDateString()}`
											: 'Filter by Target Close Date'}
										{!targetCloseDateRange.targetCloseDateStart && !targetCloseDateRange.targetCloseDateEnd ? (
											<ChevronDown />
										) : null}
									</Button>
								</PopoverTrigger>
								{targetCloseDateRange.targetCloseDateStart || targetCloseDateRange.targetCloseDateEnd ? (
									<Button
										variant='outline'
										onClick={() => setTargetCloseDateRange({ targetCloseDateStart: null, targetCloseDateEnd: null })}
									>
										<XIconNew />
									</Button>
								) : null}
							</ButtonGroup>
						</div>
						<PopoverContent styles={[bs.wAuto, bs.overflowHidden, bs.p0]} align='start'>
							<Calendar
								mode='range'
								selected={{
									from: targetCloseDateRange.targetCloseDateStart,
									to: targetCloseDateRange.targetCloseDateEnd,
								}}
								numberOfMonths={2}
								timeZone='UTC'
								onSelect={r =>
									setTargetCloseDateRange({ targetCloseDateStart: r?.from ?? null, targetCloseDateEnd: r?.to ?? null })
								}
							/>
						</PopoverContent>
					</Popover>
				)}
				{isPastDemosTab && (
					<TagCombobox
						tagName={tag ?? undefined}
						key={`tag-${tag}`}
						onValueChange={selectedTag => setTag((selectedTag as IAccountTag)?.name ?? null)}
					>
						<div className={css(styleSheet.filterGroup)}>
							<label htmlFor={`${id}-tag`} className={css(styleSheet.label)}>
								Tag
							</label>
							<ComboboxInput placeholder='Filter by tag' id={`${id}-tag`} showClear />
						</div>
						<ComboboxContent>
							<TagComboboxStatus />
							<ComboboxList>
								{(tagItem: IAccountTag) => <TagComboboxItem key={`tag-item-${tagItem.id}`} tag={tagItem} />}
							</ComboboxList>
						</ComboboxContent>
					</TagCombobox>
				)}
				{!isPastDemosTab && (
					<div className={css(styleSheet.filterGroup)}>
						<label htmlFor={`${id}-outcome-select`} className={css(styleSheet.label)}>
							Outcome
						</label>
						<Select
							value={outcomeName}
							onValueChange={handleOutcomeChange}
							disabled={demoStatus !== DemoStatusFilter.DemosToConfirm}
							items={outcomeItems}
						>
							<SelectTrigger id={`${id}-outcome-select`} styles={[bs.wFull]}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{outcomeItems.map(outcomeItem => (
									<SelectItem key={`${id}-outcome-${outcomeItem.value}`} value={outcomeItem.value}>
										{outcomeItem.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				)}
				{hasActiveFilters && (
					<Button styles={[styleSheet.clearFiltersButton]} variant='secondary' onClick={handleClearAllFilters}>
						Clear all filters
					</Button>
				)}
			</div>
		</>
	);
};
